import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAgentReport } from '../../../../contexts/AgentReportContext';
import TinyAudioPlayer from '../../../../components/common/TinyAudioPlayer';
import DateRangeFilter from '../../../../components/common/DateRangeFilter';
import Pagination from '../../../../components/common/Pagination';
import dayjs from 'dayjs';

// Default date range configuration
const defaultDateRange = {
  filter: { id: 'This Month', label: 'This Month' },
  currentStart: dayjs().startOf('month'),
  currentEnd: dayjs().endOf('month'),
  compareStart: null,
  compareEnd: null,
};

const RecentActivities = ({ agentId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchSingleAgentRecentCalls } = useAgentReport();
  
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [pagination, setPagination] = useState(null);
  const [dateRangeState, setDateRangeState] = useState(defaultDateRange);

  const totalCallsCount = useMemo(() => {
    if (pagination?.total_count != null) {
      return pagination.total_count;
    }
    return recentActivities?.length || 0;
  }, [pagination, recentActivities]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Answered':
      case 'ANS':
        return 'success';
      case 'No Answer':
      case 'NA':
        return 'warning';
      case 'Voicemail':
      case 'VM':
        return 'info';
      case 'Missed':
      case 'MSD':
      case 'Declined':
      case 'DEC':
        return 'error';
      case 'Call Back':
      case 'CB':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (rangeObj) => {
    setDateRangeState(rangeObj);
    setCurrentPage(1); // Reset to first page when date range changes
  };
  
  const fetchRecentActivities = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const response = await fetchSingleAgentRecentCalls(agentId, startDate, endDate, currentPage, pageSize);
      
      // Handle both old format (array) and new format (object with calls and pagination)
      if (Array.isArray(response)) {
        setRecentActivities(response);
        setPagination(null);
      } else if (response && response.calls) {
        setRecentActivities(response.calls || []);
        setPagination(response.pagination || null);
      } else {
        setRecentActivities([]);
        setPagination(null);
      }
      setError(null);
    } catch (error) {
      setError(error);
      setRecentActivities([]);
      setPagination(null);
      console.error('Error fetching recent activities:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, dateRangeState.currentStart, dateRangeState.currentEnd, currentPage, pageSize, fetchSingleAgentRecentCalls]);
  
  useEffect(() => {
    fetchRecentActivities();
  }, [fetchRecentActivities]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      flex: 1,
      minHeight: '70vh'
    }}>
      {/* Date Filters */}
      <Paper sx={{ p: 2, mb: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
        <DateRangeFilter
          onChange={handleDateRangeChange}
          dateRangeState={dateRangeState}
          showAllOption={false}
        />
      </Paper>

      <Paper sx={{ 
        borderRadius: 3, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        height: '100%'
      }}>
        <Box sx={{ pt: 3, px: 3, pb: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent Calls
              </Typography>
                {!loading && (
                  <Chip
                    label={`${totalCallsCount} total`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Latest call logs and interactions
              </Typography>
            </Box>
          </Box>
          {/* Pagination Controls */}
          {!loading && pagination && (
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 2
            }}>
              <Pagination
                totalCount={pagination.total_count || 0}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={pagination.total_pages || 1}
                hasNext={pagination.has_next || false}
                hasPrevious={pagination.has_previous || false}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Box>
          )}
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography color="error">Error loading recent calls: {error.message}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Call Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Recording</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivities && recentActivities.length > 0 ? (
                    recentActivities.map((activity) => (
                      <TableRow 
                        key={activity.id} 
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              width: 40,
                              height: 40
                            }}>
                              <PhoneIcon fontSize="small" />
                            </Avatar>
                            {activity?.lead?.id ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/leads/${activity.lead.id}`);
                                }}
                              >
                                {activity.title || activity.lead?.name || 'Unknown'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {activity.title || 'Unknown'}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {activity.date_time || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {activity.call_time || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {activity.call_type ? (
                            <Chip
                              label={activity.call_type.charAt(0).toUpperCase() + activity.call_type.slice(1)}
                              size="small"
                              sx={{
                                backgroundColor: 
                                  activity.call_type === 'incoming' 
                                    ? alpha(theme.palette.info.main, 0.1)
                                    : activity.call_type === 'outgoing'
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.error.main, 0.1),
                                color: 
                                  activity.call_type === 'incoming'
                                    ? theme.palette.info.main
                                    : activity.call_type === 'outgoing'
                                    ? theme.palette.success.main
                                    : theme.palette.error.main,
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {activity.call_duration || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {activity.recording_url ? (
                            <Box sx={{ width: 250, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TinyAudioPlayer
                                audioUrl={activity.recording_url}
                                duration={activity.call_duration}
                                sx={{ width: '100%' }}
                              />
                              {activity.summary && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontStyle: 'italic',
                                    opacity: 0.7,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.4,
                                    maxWidth: 250,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                  title={activity.summary}
                                >
                                  {activity.summary}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={activity.status_verbose || activity.status || '—'}
                            size="small"
                            color={getStatusColor(activity.status_verbose || activity.status)}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No recent calls found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RecentActivities;